/**
 * What bankers see in the LexisNexis identity report — owned by Risk Management (Bill 2026-10-06).
 *
 *   lexisnexis.display.showVerificationRiskIndex — the InstantID Verification Risk Index (0-50) is
 *     context only; the FraudPoint score is the gate, so it is hidden from bankers by default.
 *   lexisnexis.followUps.available — follow-up tools bankers may be told to use. PCB's LexisNexis
 *     API contract (Schedule A, 2026-05-12) covers InstantID, FraudPoint and Emailage only, so none
 *     are suggested unless Risk Management turns one on here.
 */

import { useCallback, useEffect, useState } from 'react';

import { REASON_MAX, REASON_MIN, findSetting, loadRiskSettings, saveRiskSetting } from '@/services/riskSettingsApi';

const KEY_RI = 'lexisnexis.display.showVerificationRiskIndex';
const KEY_FU = 'lexisnexis.followUps.available';

const TOOLS: Array<{ id: string; label: string; note: string }> = [
  { id: 'kba', label: 'InstantID Q&A (knowledge-based questions)', note: 'Not in the bank’s LexisNexis contract.' },
  { id: 'smartlinx', label: 'Find-a-Person / SmartLinx', note: 'Not in the bank’s LexisNexis contract.' },
  { id: 'phoneAddress', label: 'Phone / address lookup', note: 'Only if bankers have access to it.' },
  { id: 'location', label: 'Location lookup', note: 'Only if bankers have access to it.' },
];

export function LnDisplaySettingsCard(): JSX.Element {
  const [showRi, setShowRi] = useState(false);
  const [tools, setTools] = useState<string[]>([]);
  const [loaded, setLoaded] = useState<{ ri: boolean; tools: string[] } | null>(null);
  const [busy, setBusy] = useState(true);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const refresh = useCallback(async (): Promise<void> => {
    setBusy(true); setErr(null);
    try {
      const rows = await loadRiskSettings();
      const ri = (findSetting(rows, KEY_RI)?.settingValue ?? 'false').trim().toLowerCase() === 'true';
      const fu = (findSetting(rows, KEY_FU)?.settingValue ?? '')
        .split(',').map((s) => s.trim()).filter(Boolean);
      setShowRi(ri); setTools(fu); setLoaded({ ri, tools: fu });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not load.');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const dirty = loaded != null && (showRi !== loaded.ri || [...tools].sort().join(',') !== [...loaded.tools].sort().join(','));

  const save = async (): Promise<void> => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      if (loaded && showRi !== loaded.ri) await saveRiskSetting(KEY_RI, showRi ? 'true' : 'false', reason.trim());
      if (loaded && tools.join(',') !== loaded.tools.join(',')) await saveRiskSetting(KEY_FU, tools.join(','), reason.trim());
      setReason('');
      await refresh();
      setMsg('Saved. Bankers see the change the next time they open a report.');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed.');
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <div className="card-header">
        <h2>What bankers see in the identity report</h2>
      </div>
      {err && <p className="banner banner--err" role="alert">{err}</p>}
      {msg && <p className="banner" role="status">{msg}</p>}

      <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', margin: '8px 0 14px' }}>
        <input type="checkbox" checked={showRi} disabled={busy} onChange={(e) => { setShowRi(e.target.checked); setMsg(null); }} />
        <span>
          Show the Verification Risk Index (0–50)
          <span className="form-hint" style={{ display: 'block' }}>
            InstantID’s identity-element match score. It does not decide anything — the FraudPoint score does — so it is
            hidden from bankers unless you turn it on.
          </span>
        </span>
      </label>

      <div style={{ fontWeight: 600, marginBottom: 6 }}>Follow-up tools bankers may be pointed to</div>
      <p className="form-hint" style={{ marginTop: 0 }}>
        Only tick a tool bankers can actually use. Unticked tools are never suggested.
      </p>
      {TOOLS.map((t) => (
        <label key={t.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6 }}>
          <input
            type="checkbox"
            checked={tools.includes(t.id)}
            disabled={busy}
            onChange={(e) => {
              setMsg(null);
              setTools((cur) => (e.target.checked ? [...cur, t.id] : cur.filter((x) => x !== t.id)));
            }}
          />
          <span>{t.label}<span className="form-hint"> — {t.note}</span></span>
        </label>
      ))}

      {dirty && (
        <label className="form-field" style={{ marginTop: 12 }}>
          <span className="form-field__label">Reason for the change (required, saved to the change log)</span>
          <input type="text" maxLength={REASON_MAX} value={reason} disabled={busy} onChange={(e) => setReason(e.target.value)} />
        </label>
      )}

      <div className="modal__footer" style={{ paddingLeft: 0, paddingRight: 0 }}>
        <button type="button" className="btn btn--primary" disabled={busy || !dirty || reason.trim().length < REASON_MIN} onClick={() => void save()}>
          {busy ? 'Working…' : 'Save'}
        </button>
      </div>
    </section>
  );
}
